/**
 * Shared, dependency-free date formatting helpers for the frontend.
 *
 * These mirror the backend `formatDatePtBr` guards (backend/routes/complaints.js)
 * but return the dash sentinel '—' (instead of '') so invalid/missing timestamps
 * render cleanly on cards, tables, and modal headers — and NEVER leak the Unix
 * epoch (01/01/1970 / 31/12/1969).
 *
 * Dual module shape: authored as ESM `export const` for the CRA/webpack build,
 * with a trailing CommonJS `module.exports` guard so Node's test runner (which
 * uses `require`) can validate the REAL logic in backend/test/bug-exploration.test.js.
 */

const DASH = '—';

/**
 * Returns true when `value` is a missing/invalid/epoch-resolving timestamp.
 */
const isInvalidDateValue = (value) => {
  if (value === null || value === undefined || value === '') return true;
  if (value === 0 || value === '0') return true;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return true;
  if (parsed.getTime() === 0) return true; // Unix epoch guard
  return false;
};

/**
 * Format a value as a pt-BR date, or '—' when the value is null/undefined/'',
 * 0/'0', unparseable, or resolves to the Unix epoch.
 */
export const formatDateOrDash = (value) => {
  if (isInvalidDateValue(value)) return DASH;
  return new Date(value).toLocaleDateString('pt-BR');
};

/**
 * Same guards as formatDateOrDash, but renders date + time (pt-BR).
 */
export const formatDateTimeOrDash = (value) => {
  if (isInvalidDateValue(value)) return DASH;
  return new Date(value).toLocaleString('pt-BR');
};

// CommonJS fallback so Node's test runner (require) can validate the real logic.
// CRA/webpack tolerates this trailing guard; the ESM exports above drive the build.
if (typeof module !== 'undefined' && module.exports) {
  module.exports = { formatDateOrDash, formatDateTimeOrDash };
}
